import React from 'react';
import { Sample } from '../../../@types/samples/types';
import { AnalysisMethod, SampleRunResult, isKrMethod } from '../types';
import { LAMBDA_METHOD_LABELS } from '../../../components/dashboard/components/LambdaAnalysis/methodConstants';
import FitSparkline from './FitSparkline';
import StatusPill from './StatusPill';
import styles from '../MultiAnalysisPage.module.css';
import { toBrooksCoreyPoreSizeDistributionIndex } from '../../../utils/brooksCorey';

type Props = {
  sample: Sample;
  result?: SampleRunResult;
  checked: boolean;
  /** Active batch methodology — drives params, fit curve and Pce input. */
  method: AnalysisMethod;
  /** Sample already had stored results for the active methodology. */
  overwrites?: boolean;
  /** Pre-run data status of the sample for the selected analysis category. */
  dataStatus?: 'analyzed' | 'input-only' | 'no-data';
  /** Y (Pc) axis scale for the mini chart. */
  logScale?: boolean;
  /** Editable Pce input value + change handler for this sample (Liao only). */
  pce?: string;
  onPceChange?: (value: string) => void;
  onToggleChecked: (uid: string) => void;
  onInspect: (uid: string) => void;
};

const formatNumber = (value?: number | null, digits = 4) =>
  typeof value === 'number' && Number.isFinite(value)
    ? value.toFixed(digits)
    : '—';

/**
 * One IP-style calibration panel: include-checkbox, sample title, SVG fit
 * preview, status pill and the active methodology's key parameters. Clicking
 * the card opens the detail view.
 */
const SampleThumbnail = ({
  sample,
  result,
  checked,
  method,
  overwrites,
  dataStatus,
  logScale,
  pce,
  onPceChange,
  onToggleChecked,
  onInspect,
}: Props) => {
  const values = result?.sample?.analysis_values ?? sample.analysis_values;
  const liao = values?.liao;
  const lambda = values?.lambda_values;
  const outcome =
    result?.sample?.analysis_outcomes?.[method] ??
    sample.analysis_outcomes?.[method];
  const displaySample = result?.sample ?? sample;
  const krValues = displaySample.k_rel_values;
  const waterOil = krValues?.water_oil;
  const gasOil = krValues?.gas_oil;

  return (
    <div
      className={styles.thumbnail}
      role="button"
      tabIndex={0}
      onClick={() => onInspect(sample.uid)}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onInspect(sample.uid);
        }
      }}
    >
      <div className={styles.thumbnailHeader}>
        <label
          className={styles.thumbnailCheck}
          onClick={event => event.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onToggleChecked(sample.uid)}
            aria-label={`Include ${sample.sample_code} in batch actions`}
          />
        </label>
        <span className={styles.thumbnailTitle} title={sample.sample_code}>
          {sample.sample_code} · {sample.depth}
          {sample.depth_unit ?? 'ft'}
        </span>
        {result ? (
          <StatusPill status={result.status} reviewed={result.reviewed} />
        ) : null}
      </div>

      <div className={styles.thumbnailTags}>
        {result?.saved ? (
          <span className={`${styles.tag} ${styles.tagSaved}`}>Saved</span>
        ) : result && result.status !== 'failed' ? (
          <span className={`${styles.tag} ${styles.tagPreview}`}>Preview</span>
        ) : dataStatus === 'analyzed' ? (
          <span className={`${styles.tag} ${styles.tagAnalyzed}`}>Analyzed</span>
        ) : dataStatus === 'input-only' ? (
          <span className={`${styles.tag} ${styles.tagInputOnly}`}>
            Input only
          </span>
        ) : (
          <span
            className={`${styles.tag} ${styles.tagNoData}`}
            title="No input data or result for this analysis"
          >
            No data
          </span>
        )}
        {overwrites && result && !result.saved ? (
          <span
            className={`${styles.tag} ${styles.tagOverwrite}`}
            title="This sample already has stored results for this analysis. Saving will overwrite them."
          >
            Overwrites saved
          </span>
        ) : null}
        {method === 'lambda' &&
        outcome?.candidate_type === 'non_contiguous' ? (
          <span
            className={`${styles.tag} ${styles.tagOutlier}`}
            title="An internal point was automatically excluded as a suspected outlier."
          >
            Outlier excluded
          </span>
        ) : null}
        {method === 'lambda' && lambda?.method ? (
          <span
            className={`${styles.tag} ${styles.tagMethod}`}
            title="Brooks-Corey solver method used for this fit"
          >
            {LAMBDA_METHOD_LABELS[lambda.method]}
          </span>
        ) : null}
        {isKrMethod(method) && outcome?.candidate_type === 'refined' ? (
          <span
            className={`${styles.tag} ${styles.tagMethod}`}
            title="Auto-refinement computed true Sor, endpoint and Corey exponents"
          >
            Refined
          </span>
        ) : null}
        {isKrMethod(method) && outcome?.candidate_type === 'preserved' ? (
          <span
            className={`${styles.tag} ${styles.tagOutlier}`}
            title="Existing manual refinement kept; auto-refinement did not replace it"
          >
            Manual kept
          </span>
        ) : null}
      </div>

      <FitSparkline sample={displaySample} method={method} logScale={logScale} />

      {method === 'lambda' ? (
        <div className={styles.thumbnailParams}>
          <span>Pe {formatNumber(lambda?.pe)}</span>
          <span>m {formatNumber(lambda?.lambda_value)}</span>
          <span>
            λBC {formatNumber(toBrooksCoreyPoreSizeDistributionIndex(lambda?.lambda_value))}
          </span>
          <span>R² {formatNumber(outcome?.r_squared)}</span>
          <span>RMSE {formatNumber(lambda?.fit_rmse, 2)}</span>
          {outcome?.n_fit_points != null ? (
            <span>
              pts {outcome.n_fit_points}
              {outcome.n_total_points != null
                ? `/${outcome.n_total_points}`
                : ''}
            </span>
          ) : null}
        </div>
      ) : method === 'water-oil' ? (
        <div className={styles.thumbnailParams}>
          <span>
            Sor {formatNumber(waterOil?.true_sor_w ?? waterOil?.sor_w, 3)}
          </span>
          <span>No {formatNumber(waterOil?.true_no_w ?? waterOil?.no_w, 2)}</span>
          <span>Nw {formatNumber(waterOil?.true_nw ?? waterOil?.nw, 2)}</span>
          <span>
            Krw&apos;{' '}
            {formatNumber(waterOil?.true_krw ?? waterOil?.krw_exponent, 3)}
          </span>
          {outcome?.exponent_sum != null ? (
            <span>Σ {formatNumber(outcome.exponent_sum, 2)}</span>
          ) : null}
        </div>
      ) : method === 'gas-oil' ? (
        <div className={styles.thumbnailParams}>
          <span>
            Sorg {formatNumber(gasOil?.true_sor_g ?? gasOil?.sor_g, 3)}
          </span>
          <span>No {formatNumber(gasOil?.true_no_g ?? gasOil?.no_g, 2)}</span>
          <span>Ng {formatNumber(gasOil?.true_ng ?? gasOil?.ng, 2)}</span>
          <span>
            Krg&apos;{' '}
            {formatNumber(gasOil?.true_krg ?? gasOil?.krg_exponent, 3)}
          </span>
          {outcome?.exponent_sum != null ? (
            <span>Σ {formatNumber(outcome.exponent_sum, 2)}</span>
          ) : null}
        </div>
      ) : (
        <div className={styles.thumbnailParams}>
          <span>a {formatNumber(liao?.best_a)}</span>
          <span>b {formatNumber(liao?.best_b, 2)}</span>
          <span>Pce {formatNumber(liao?.pce, 6)}</span>
          <span>RMSE {formatNumber(outcome?.fit_rmse)}</span>
        </div>
      )}

      {method === 'liao' ? (
        <label
          className={styles.thumbnailPce}
          onClick={event => event.stopPropagation()}
          onKeyDown={event => event.stopPropagation()}
        >
          <span>Pce</span>
          <input
            type="number"
            step="0.000001"
            min="0"
            value={pce ?? ''}
            placeholder="Auto"
            onChange={event => onPceChange?.(event.target.value)}
            aria-label={`Pce for ${sample.sample_code}`}
          />
        </label>
      ) : null}
    </div>
  );
};

export default SampleThumbnail;
